import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { saveToR2 } from '@/api/r2';

const StarRating = ({ value, onChange }) => (
  <div className="flex items-center gap-1">
    {[...Array(10)].map((_, i) => (
      <Star
        key={i}
        className={`w-6 h-6 cursor-pointer transition-colors ${
          i < value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
        }`}
        onClick={() => onChange(i + 1)}
      />
    ))}
  </div>
);

export default function LessonFeedbackModal({ onSubmit, onClose, lessonId: propLessonId }) {
  const [enjoyment, setEnjoyment] = useState(0);
  const [understanding, setUnderstanding] = useState(0);
  const [suggestion, setSuggestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fallback: leggi id lezione da query string se non passa via prop
  const lessonId =
    propLessonId ??
    (() => {
      try {
        return new URLSearchParams(window.location.search).get('id');
      } catch {
        return null;
      }
    })();

  const handleSubmit = async () => {
    if (enjoyment === 0 || understanding === 0) return;

    setIsSubmitting(true);
    const data = {
      rating_enjoyment: enjoyment,
      rating_understanding: understanding,
      improvement_suggestion: suggestion?.trim() || null,
      submitted_at: new Date().toISOString(),
    };

    try {
      // Salva su R2 (con coda offline gestita da saveToR2)
      await saveToR2('lesson_feedback', {
        lesson_id: lessonId || undefined,
        data,
        meta: { source: 'feedback_modal' },
      });
    } catch (err) {
      console.warn('[R2] lesson_feedback: queued/offline', err);
      // Non blocchiamo l’utente se offline
    }

    try {
      // Mantieni la compatibilità con la logica esistente della pagina
      if (typeof onSubmit === 'function') {
        await onSubmit({
          rating_enjoyment: data.rating_enjoyment,
          rating_understanding: data.rating_understanding,
          improvement_suggestion: data.improvement_suggestion,
        });
      }
    } finally {
      setIsSubmitting(false);
      if (typeof onClose === 'function') onClose();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-6"
      >
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">Feedback Lezione</h2>
          <p className="text-gray-500">Il tuo parere è importante per migliorare!</p>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="font-semibold text-lg">Ti è piaciuta la lezione?</Label>
            <StarRating value={enjoyment} onChange={setEnjoyment} />
          </div>
          <div>
            <Label className="font-semibold text-lg">Quanto hai capito la lezione?</Label>
            <StarRating value={understanding} onChange={setUnderstanding} />
          </div>
          <div>
            <Label htmlFor="suggestion" className="font-semibold text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Miglioreresti qualcosa?
            </Label>
            <Textarea
              id="suggestion"
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              placeholder="Scrivi qui i tuoi suggerimenti..."
              rows={3}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>Salta</Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || enjoyment === 0 || understanding === 0}>
            {isSubmitting ? 'Invio...' : 'Invia Feedback'}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
